import { supabase } from '../lib/supabase';
import { appLogger } from '../utils/appLogger';

// Types for logging
export interface LogEvent {
  level: 'info' | 'warn' | 'error';
  action: string;
  details?: Record<string, any>;
  error?: Error;
}

// Simple logging function that won't break the app
export const logEvent = async (event: LogEvent) => {
  try {
    // Console output only when CASHFREE_ENV=TEST (appLogger respects env)
    appLogger.info(`[${event.level.toUpperCase()}] ${event.action}:`, event.details || {});
    
    // Save to database only when authenticated (avoids 401 noise from RLS)
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    await supabase.from('system_logs').insert({
      log_level: event.level,
      log_category: categorizeAction(event.action),
      message: event.action,
      details: event.details || {},
      user_id: user.id,
      created_at: new Date().toISOString()
    });
    
  } catch (error) {
    // Silently fail - don't break the app
    appLogger.warn('Logging failed', error);
  }
};

// Helper to categorize actions into system categories
const categorizeAction = (action: string): string => {
  const lower = action.toLowerCase();
  
  if (lower.includes('login') || lower.includes('auth') || lower.includes('signup')) {
    return 'auth';
  }
  if (lower.includes('api') || lower.includes('fetch') || lower.includes('request')) {
    return 'api';
  }
  if (lower.includes('database') || lower.includes('supabase') || lower.includes('query')) {
    return 'database';
  }
  if (lower.includes('optimization') || lower.includes('optimize') || lower.includes('resume')) {
    return 'optimization';
  }
  if (lower.includes('application') || lower.includes('job')) {
    return 'application';
  }
  if (lower.includes('user_action') || lower.includes('click') || lower.includes('navigation')) {
    return 'user_action';
  }
  
  return 'system';
};

// Log application errors
export const logApplicationError = async (error: Error, additionalInfo?: string) => {
  try {
    const errorDetails = {
      name: error.name,
      message: error.message,
      stack: error.stack,
      additionalInfo
    };
    
    appLogger.error('Application error', errorDetails);
    
    // Save error to database only when authenticated
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    await supabase.from('system_logs').insert({
      log_level: 'error',
      log_category: 'system',
      message: `Application Error: ${error.message}`,
      details: errorDetails,
      user_id: user.id,
      created_at: new Date().toISOString()
    });
    
  } catch (logError) {
    // Silently fail
    appLogger.warn('Error logging failed', logError);
  }
};

// Export default for backward compatibility
export default {
  logEvent,
  logApplicationError
};
